<script>
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PromptsAPI from 'dashboard/api/prompts';
import AttributeAPI from 'dashboard/api/attributes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import RichTextEditor from '../components/RichTextEditor.vue';

export default {
  name: 'EmailTemplateEdit',
  components: {
    NextButton,
    NextInput,
    WithLabel,
    BaseSettingsHeader,
    SettingsLayout,
    RichTextEditor,
  },
  setup() {
    const { accountScopedRoute } = useAccount();
    return { accountScopedRoute };
  },
  data() {
    return {
      templateName: this.$route.params.templateName,
      contactCustomAttributes: [],
      summarization: {
        content: '',
        original: '',
        promptId: null,
      },
      template: {
        content: '',
        original: '',
        promptId: null,
        bcc_email: '',
        bcc_email_original: '',
      },
      isLoading: false,
      isSaving: {
        summarization: false,
        template: false,
      },
      isInitializing: true,
      availableVariables: [
        { name: 'contact.name', label: 'Contact Name' },
        { name: 'contact.email', label: 'Contact Email' },
        { name: 'contact.company_name', label: 'Contact Company' },
        { name: 'contact.city', label: 'Contact City' },
        { name: 'contact.country', label: 'Contact Country' },
        { name: 'contact.phone_number', label: 'Contact Phone' },
        { name: 'conversation.display_id', label: 'Conversation ID' },
        { name: 'conversation.status', label: 'Status' },
        { name: 'conversation.priority', label: 'Priority' },
        { name: 'conversation.assignee_name', label: 'Assignee' },
        { name: 'conversation.inbox_name', label: 'Inbox' },
        { name: 'event.name', label: 'Event Name' },
        { name: 'event.start_date', label: 'Event Start Date' },
        { name: 'event.end_date', label: 'Event End Date' },
        { name: 'event.pax', label: 'Event Guests (pax)' },
        { name: 'event.space_names', label: 'Event Spaces' },
        { name: 'event.system_stage', label: 'Event Stage' },
      ],
    };
  },
  computed: {
    templateTitle() {
      const titles = {
        transcript: this.$t('EMAIL_TEMPLATES.TRANSCRIPT.TITLE'),
        confirmation: this.$t('EMAIL_TEMPLATES.CONFIRMATION.TITLE'),
      };
      return titles[this.templateName] || this.templateName;
    },
    isCustomTemplate() {
      return !['transcript', 'confirmation'].includes(this.templateName);
    },
    bccVariables() {
      const standard = [{ name: 'contact.email', label: 'Contact Email' }];
      const custom = this.contactCustomAttributes
        .filter(attr => attr.attribute_display_type === 'text')
        .map(attr => ({
          name: `contact.custom.${attr.attribute_key}`,
          label: attr.attribute_display_name,
        }));
      return [...standard, ...custom];
    },
    hasUnsavedSummarization() {
      if (this.isInitializing) return false;
      return this.summarization.content !== this.summarization.original;
    },
    hasUnsavedTemplate() {
      if (this.isInitializing) return false;
      return (
        this.template.content !== this.template.original ||
        this.template.bcc_email !== this.template.bcc_email_original
      );
    },
  },
  watch: {
    '$route.params.templateName': {
      handler(newName) {
        if (newName) {
          this.templateName = newName;
          this.isInitializing = true;
          this.loadTemplates();
        }
      },
    },
  },
  mounted() {
    this.loadTemplates();
    this.loadContactCustomAttributes();
  },
  methods: {
    async loadTemplates() {
      this.isLoading = true;
      try {
        const response = await PromptsAPI.get(null, 'markdown');
        const prompts = response.data || [];

        // Load summarization prompt
        const summPrompt = prompts.find(
          p => p.name === this.templateName && p.type === 'summarization'
        );

        if (summPrompt) {
          this.summarization.content = summPrompt.content;
          this.summarization.original = summPrompt.content;
          this.summarization.promptId = summPrompt.id;
          this.$refs.summarizationEditor?.setContent(summPrompt.content);
        } else {
          const defaultResp = await PromptsAPI.getDefault(
            'summarization',
            'markdown'
          );
          const defaultContent = defaultResp.data.content;
          this.summarization.promptId = null;
          this.summarization.content = defaultContent;
          this.summarization.original = '';
          this.$refs.summarizationEditor?.setContent(defaultContent);
        }

        // Load subject line template
        const templatePrompt = prompts.find(
          p => p.name === this.templateName && p.type === 'template'
        );

        if (templatePrompt) {
          this.template.content = templatePrompt.content;
          this.template.original = templatePrompt.content;
          this.template.promptId = templatePrompt.id;
          this.template.bcc_email = templatePrompt.bcc_email || '';
          this.template.bcc_email_original = templatePrompt.bcc_email || '';
        } else {
          const defaultResp = await PromptsAPI.getDefault(
            'template',
            'markdown'
          );
          const defaultContent = defaultResp.data.content;
          this.template.promptId = null;
          this.template.content = defaultContent;
          this.template.original = '';
          this.template.bcc_email = '';
          this.template.bcc_email_original = '';
        }

        this.isInitializing = false;
      } catch (error) {
        this.isInitializing = false;
        useAlert(this.$t('EMAIL_TEMPLATES.LOAD_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },

    onSummarizationUpdate(html) {
      if (!this.isInitializing) {
        this.summarization.content = html;
      }
    },

    async saveTemplate(type) {
      const data = this[type];
      this.isSaving[type] = true;

      try {
        const payload = {
          name: this.templateName,
          type: type,
          content: data.content,
          ...(type === 'template' ? { bcc_email: data.bcc_email } : {}),
        };

        if (data.promptId) {
          await PromptsAPI.update(data.promptId, payload, 'markdown');
        } else {
          const response = await PromptsAPI.create(payload, 'markdown');
          data.promptId = response.data.id;
        }

        data.original = data.content;
        useAlert(this.$t('EMAIL_TEMPLATES.SAVE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('EMAIL_TEMPLATES.SAVE_ERROR'));
      } finally {
        this.isSaving[type] = false;
      }
    },

    async resetTemplate(type) {
      const data = this[type];
      this.isSaving[type] = true;

      try {
        if (data.promptId) {
          await PromptsAPI.delete(data.promptId);
        }

        const defaultResp = await PromptsAPI.getDefault(type, 'markdown');
        const defaultContent = defaultResp.data.content;

        data.content = defaultContent;
        data.original = '';
        data.promptId = null;

        if (type === 'template') {
          data.bcc_email = '';
          data.bcc_email_original = '';
        }

        if (type === 'summarization') {
          this.$refs.summarizationEditor?.setContent(defaultContent);
        }

        useAlert(this.$t('EMAIL_TEMPLATES.RESET_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('EMAIL_TEMPLATES.RESET_ERROR'));
      } finally {
        this.isSaving[type] = false;
      }
    },

    insertVariable(variable) {
      const variableText = `{${variable.name}}`;
      this.template.content = (this.template.content || '') + variableText;
    },

    insertVariableToBcc(variable) {
      const variableText = `{${variable.name}}`;
      this.template.bcc_email = (this.template.bcc_email || '') + variableText;
    },

    async loadContactCustomAttributes() {
      try {
        const response = await AttributeAPI.getAttributesByModel();
        this.contactCustomAttributes = (response.data || []).filter(
          attr => attr.attribute_model === 'contact_attribute'
        );
      } catch {
        // silently ignore — chips simply won't show for custom attributes
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('EMAIL_TEMPLATES.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="templateTitle"
        :back-button-label="$t('EMAIL_TEMPLATES.BACK')"
      />
    </template>
    <template #body>
      <div class="max-w-4xl">
        <!-- Subject Line Template Section -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold mb-2">
            {{ $t('EMAIL_TEMPLATES.SUBJECT_LINE_TEMPLATE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-3">
            {{ $t('EMAIL_TEMPLATES.SUBJECT_HELP') }}
          </p>

          <!-- Variable chips -->
          <div class="mb-3 flex flex-wrap gap-2">
            <button
              v-for="variable in availableVariables"
              :key="variable.name"
              type="button"
              class="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-100 rounded-full hover:bg-slate-75 dark:hover:bg-slate-600 transition-colors"
              @click="insertVariable(variable)"
            >
              <span class="i-lucide-plus size-3" />
              {{ variable.label }}
            </button>
          </div>

          <WithLabel name="subject-template">
            <NextInput
              v-model="template.content"
              type="text"
              class="w-full"
              :placeholder="$t('EMAIL_TEMPLATES.SUBJECT_PLACEHOLDER')"
            />
          </WithLabel>
          <div class="mt-4">
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('EMAIL_TEMPLATES.BCC_EMAIL_LABEL') }}
            </h4>
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-2">
              {{ $t('EMAIL_TEMPLATES.BCC_EMAIL_HELP') }}
            </p>
            <div v-if="bccVariables.length" class="mb-3 flex flex-wrap gap-2">
              <button
                v-for="variable in bccVariables"
                :key="variable.name"
                type="button"
                class="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-100 rounded-full hover:bg-slate-75 dark:hover:bg-slate-600 transition-colors"
                @click="insertVariableToBcc(variable)"
              >
                <span class="i-lucide-plus size-3" />
                {{ variable.label }}
              </button>
            </div>
            <WithLabel name="bcc-email">
              <NextInput
                v-model="template.bcc_email"
                type="text"
                class="w-full"
                :placeholder="$t('EMAIL_TEMPLATES.BCC_EMAIL_PLACEHOLDER')"
              />
            </WithLabel>
          </div>
          <div class="mt-3 flex gap-2 py-4">
            <NextButton
              blue
              :loading="isSaving.template"
              :disabled="!hasUnsavedTemplate"
              @click="saveTemplate('template')"
            >
              {{ $t('EMAIL_TEMPLATES.SAVE') }}
            </NextButton>
            <NextButton
              variant="outline"
              ruby
              :loading="isSaving.template"
              @click="resetTemplate('template')"
            >
              {{ $t('EMAIL_TEMPLATES.RESET') }}
            </NextButton>
          </div>
        </div>

        <!-- Summarization Prompt Section -->
        <div>
          <h3 class="text-lg font-semibold mb-2">
            {{ $t('EMAIL_TEMPLATES.SUMMARIZATION_PROMPT') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {{ $t('EMAIL_TEMPLATES.SUMMARIZATION_HELP') }}
          </p>
          <RichTextEditor
            ref="summarizationEditor"
            :model-value="summarization.content"
            @update:model-value="onSummarizationUpdate"
          />
          <div class="mt-3 flex gap-2 py-4">
            <NextButton
              blue
              :loading="isSaving.summarization"
              :disabled="!hasUnsavedSummarization"
              @click="saveTemplate('summarization')"
            >
              {{ $t('EMAIL_TEMPLATES.SAVE') }}
            </NextButton>
            <NextButton
              variant="outline"
              ruby
              :loading="isSaving.summarization"
              @click="resetTemplate('summarization')"
            >
              {{ $t('EMAIL_TEMPLATES.RESET') }}
            </NextButton>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
